import { normalizeForMatch } from "../lib/textNormalize";

// 発話が「回答として成立していない」ことを端末側だけで即断する (spec 2026-07-28 §3.2)。
//
// この関数は「回答である」とは判定しない。明らかな非回答だけを救い出し、
// 残りはすべて unsure として下流 (構造化 LLM の verdict) に委ねる。
//
// 誤判定の非対称性が設計の中心にある:
//   誤って回答を弾く → ユーザーが言い直しを強いられる (直そうとしている症状の再生産)
//   無内容な発話を見逃す → 現状の挙動。悪化しない
// したがって語彙はすべて「弾かない側」に倒してある。
//
// interruptionJudge (読み上げ中の割り込み判定) とは**語彙を共有しない**。
// あちらの語彙は「読み上げ中の割り込みが本物の回答か」に最適化されており、
// ここに「あー」を足すと、読み上げ中に「あー」と言った人の割り込みが効かなくなる
// という別の変更が同時に起きる。yesNoIntent と interruptionJudge が FILLERS を
// 共有していないのと同じ判断 (割り込み判定 spec §3.4.5)。

export type AnswerRejectReason = "empty" | "filler";

export type AnswerVerdict = {
  verdict: "not-answer" | "unsure";
  reason?: AnswerRejectReason;
};

// 長音符。ASCII の `~` を含むのは、normalizeForMatch が全角記号を半角に畳むため
// (全角チルダ ～ U+FF5E はこの時点で ~ U+007E になっている)。
const PROLONG = /[ー〜~]/g;

// カタカナ (ァ〜ヶ) をひらがなへ寄せる。Scribe は引き伸ばし音をカタカナで返すことが
// あるので、語彙をひらがなだけで持つ以上ここで畳む必要がある。
// nameNormalize の toKatakana とは逆向きだが流用しない — あちらは**表示のための**
// 変換で、こちらは突き合わせ専用 (画面にも保存にも出ない)。
const KATAKANA = /[ァ-ヶ]/g;

// 語彙は「畳み込み後」の形で持つ。
//
// 「そうですね」は入れない: 日本語では相槌ではなく回答の前置きが支配的で、
// long の発話終了判定 (1800ms) で切られると「答えを組み立て始めた瞬間に弾かれる」。
// 「わかりません」「特にありません」も入れない: long 設問では正当な回答。
// 「い」は入れない: 畳み込みで「いい」が `い` になるため。得るもの (「いー」は
// ほぼ現れない) より失うもの (薄くても回答でありうる「いい」) が大きい。
const NON_ANSWERS = new Set([
  // 引き伸ばし音
  "あ",
  "う",
  "え",
  "お",
  "ん",
  // フィラー
  "えと",
  "えっと",
  "あの",
  "まあ",
  "うんと",
  // 相槌
  "うん",
  "うんうん",
  "はい",
  "へ",
  "ほ",
  "なるほど",
  "わかりました",
]);

function collapse(s: string): string {
  return s
    .replace(KATAKANA, (c) => String.fromCharCode(c.charCodeAt(0) - 0x60))
    .replace(PROLONG, "")
    .replace(/(.)\1+/gu, "$1");
}

export function judgeAnswer(transcript: string): AnswerVerdict {
  const collapsed = collapse(normalizeForMatch(transcript));
  if (collapsed.length === 0) return { verdict: "not-answer", reason: "empty" };
  // 完全一致だけを見る。部分一致にすると「うん、今週は資料を作っていて…」のような
  // 本物の回答を、先頭の相槌だけで弾く。
  if (NON_ANSWERS.has(collapsed)) return { verdict: "not-answer", reason: "filler" };
  return { verdict: "unsure" };
}
